import { ATTENTION_TIERS } from "@healthos/shared";
import { z } from "zod";
import { CaptureLogActionSchema, CapturePreselectSchema } from "./capturePrompt.js";
/**
 * Wire schemas for the LLM-fed surfaces on `/today`.
 *
 * Why these exist as Zod schemas (not just TS interfaces):
 *
 *   The Weekly Brief, Outcome trajectory, Environment context, and
 *   Score readout all flow from the API as JSON; in many cases the
 *   *content* (brief headline, narrative body, status copy, weather
 *   nudge) is LLM output. TypeScript catches shape drift at the seam
 *   only at compile time. A Zod parse at the page boundary catches
 *   shape drift at *runtime*: when an agent emits `score: null` or
 *   `host: { name: undefined }`, the page surfaces a useful error
 *   instead of crashing mid-render six React levels deep.
 *
 *   Pages call `AmbientStateSchema.safeParse(json)` and either render
 *   the validated payload or fall back to the empty / placeholder
 *   state. The schemas are also the canonical contract - when the
 *   server-side `/api/ambient` shape changes, this is where it gets
 *   updated, and TypeScript propagates the type.
 *
 * Naming convention: `*Schema` for the runtime parser, plain type
 * name for the inferred TS type (`type BriefCopy = z.infer<...>`).
 */
export const BriefHostSchema = z.object({
    name: z.string().min(1),
    role: z.string().optional(),
    organization: z.string().optional(),
    tagline: z.string().optional(),
    /** Absolute URL or app-relative path; component falls back to initials. */
    photoUrl: z.string().optional(),
});
export const BriefSectionSchema = z.object({
    overline: z.string(),
    title: z.string(),
    body: z.string(),
});
export const BriefBodySegmentSchema = z.object({
    text: z.string(),
    highlight: z.boolean().optional(),
    emphasis: z.boolean().optional(),
});
export const BriefCopySchema = z.object({
    eyebrow: z.string().optional(),
    date: z.string().optional(),
    headline: z.string(),
    subtitle: z.string().optional(),
    durationLabel: z.string().optional(),
    episodeLabel: z.string().optional(),
    updatedLabel: z.string().optional(),
    host: BriefHostSchema.optional(),
    body: z.union([z.string(), z.array(BriefBodySegmentSchema)]).optional(),
    sections: z.array(BriefSectionSchema).optional(),
    sources: z.array(z.string()).optional(),
    /* Server emits null while the audio is still rendering / hasn't been
     * generated yet. Accept both. SPA gates `<audio src>` on truthiness,
     * so null reads the same as missing at render time. */
    audioUrl: z.string().nullable().optional(),
    /** Persisted brief id. Null on the welcome placeholder that has no row. */
    id: z.string().uuid().nullable().optional(),
    /** ISO timestamp when the member finished listening. Null until then. */
    playedAt: z.string().nullable().optional(),
});
export const AmbientScorePayloadSchema = z.object({
    value: z.number().min(0).max(100),
    max: z.number().positive(),
    label: z.string(),
    heading: z.string(),
    mode: z.enum(["default", "survival", "gathering"]).optional(),
    aboveFloor: z.boolean().optional(),
    /**
     * The five behaviours with their own reads, so Today's card is a small
     * Snapshot rather than a row of icons. A behaviour running on its neutral
     * default reports null, never a number nobody measured.
     */
    behaviours: z.array(z.object({ id: z.string(), score: z.number().nullable() })).optional(),
    // The fortnightly wellbeing check-in: the server decides when it is due, and
    // the Today surface on every client prompts from this one answer.
    wellbeingCheckIn: z
        .object({
        due: z.boolean(),
        // What made it due: never answered, or the routine interval has passed.
        // Null when it is not due.
        reason: z.enum(["never", "interval"]).nullable().optional(),
        lastAt: z.string().nullable(),
        daysSince: z.number().nullable(),
        intervalDays: z.number(),
    })
        .optional(),
});
export const WatchingRowSchema = z.object({
    label: z.string(),
    value: z.string(),
    /**
     * The signal behind the row. Surfaces pick the row's icon from this rather
     * than from the label, which is member-facing copy: keying on the text meant
     * only the one label that had been hard-coded ("weather") ever drew an icon.
     */
    kind: z.string().optional(),
});
export const NextActionMediaSchema = z.object({
    videoUrl: z.string().nullable().optional(),
    thumbnailUrl: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    durationSeconds: z.number().int().nonnegative().nullable().optional(),
    estimatedReadSeconds: z.number().int().nonnegative().nullable().optional(),
});
export const RecommendedResetSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    description: z.string(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    videoUrl: z.string().min(1),
    thumbnailUrl: z.string().min(1).nullable(),
});
export const RecommendedEpisodeSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    description: z.string(),
    showTitle: z.string().nullable(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    audioUrl: z.string().min(1),
    thumbnailUrl: z.string().min(1).nullable(),
});
export const NextActionPayloadSchema = z.object({
    kind: z.enum([
        "reset",
        "guide",
        "story",
        "recipe",
        "quiet_check",
        "reinforcement",
        // Capture prompt: a "log this now" CTA that opens a manual-log form. Copy is
        // static (from the template), the tap routes via the `capture` field below.
        "capture",
        "moment",
        // Day Zero. Decided in code, never by the agent. "tour" hands the member
        // to another screen; "comprehension" is the closing question.
        "tour",
        "comprehension",
        // The week's Brief, waiting to be listened to. Decided in code the same
        // way: it is a thing the member has, not a moment Heartbeat chose.
        "brief",
    ]),
    /** Always set for renderable actions; missing only on a placeholder. */
    actionId: z.string().optional(),
    /** Canonical action sentence. */
    title: z.string(),
    /** Presentation-only grounded topline; card falls back to `title`. */
    headline: z.string().optional(),
    /** LLM-generated first-person observation grounded in the user's signals. Shown as context in the card. */
    noticed: z.string().optional(),
    /** The "why now" body. */
    description: z.string(),
    /** Plain timing line for the action, e.g. "When you get into bed." */
    when: z.string().optional(),
    /** Heartbeat agent's rationale text (the user-visible "Why now" caption). */
    rationale: z.string().optional(),
    /** How loud the card is allowed to be. Defaults to routine when absent. */
    tier: z.enum(ATTENTION_TIERS).optional(),
    /** Behaviour bucket for the eyebrow. */
    category: z.enum(["sleep", "nutrition", "movement", "stress", "connection"]).optional(),
    /** Estimated duration in minutes. */
    durationMin: z.number().int().positive().optional(),
    /** Plain-text progress hint ("Day 1 of 7", or "First commitment"). */
    progressLabel: z.string(),
    /** Real signal evidence the action leaned on. When present, the SPA
     *  renders these as the "Signals we're watching" rows; when absent,
     *  the SPA falls back to persona-static chrome. */
    watching: z.array(WatchingRowSchema).optional(),
    /** Signal kind the rationale reasons from, so a surface that has room for
     *  fewer rows than it was given never drops the one the card cites. */
    triggeredBy: z.string().optional(),
    /** Per-kind media payload (reset video, story image, recipe image). */
    media: NextActionMediaSchema.optional(),
    /** Article id when kind is `story` and surfaced via content.fetch_article. */
    contentId: z.string().optional(),
    /**
     * Capture prompt routing. Present only when kind is `capture`: which manual-log
     * form the tap opens and the field to pre-select. The card copy rides the
     * normal title/description fields.
     */
    capture: z
        .object({
        logAction: CaptureLogActionSchema,
        preselect: CapturePreselectSchema.optional(),
    })
        .optional(),
    moment: z
        .object({
        capability: z.string(),
        rung: z.union([z.literal(1), z.literal(2), z.literal(3)]),
        /** Whether the member's tap or a later signal completes this moment. */
        resolution: z.enum(["tap", "signal"]).optional(),
        eyebrow: z.string(),
        primaryLabel: z.string(),
        calendarSlot: z.object({ startMinutes: z.number(), durationMinutes: z.number() }).nullable(),
        draftMessage: z.string().nullable(),
        calendarUrl: z.string().nullable(),
        /** What a tap does, and where it goes when it leaves the card. */
        handoff: z.enum(["reset", "calendar", "movement", "connection", "done"]).optional(),
        handoffUrl: z.string().nullable().optional(),
        /** The reset a reset handoff opens, so a client never reads it back out of the link. */
        resetId: z.string().nullable().optional(),
        /** The exact playable reset Heartbeat selected for this moment. */
        recommendedReset: RecommendedResetSchema.nullable().optional(),
        /** The podcast episode a walk nudge hands over for the way; its button plays it. */
        recommendedEpisode: RecommendedEpisodeSchema.nullable().optional(),
    })
        .optional(),
    /**
     * Heartbeat decision row this card came from. Echoed back when the member
     * acts on it, so an outcome can be joined to the decision, its experiment arm
     * and its propensity. Absent for cards built outside the heartbeat path.
     */
    decisionId: z.string().uuid().optional(),
    /** Legacy concatenated string. */
    message: z.string(),
});
export const AmbientGreetingSchema = z.object({
    title: z.string().min(1),
    body: z.string(),
});
export const QuietHoursStatusSchema = z.object({
    start: z.string(),
    end: z.string(),
    timezone: z.string(),
    isActive: z.boolean(),
    startsAtLabel: z.string(),
    endsAtLabel: z.string(),
});
export const PersonaCostEstimateSchema = z.object({
    amountUsd: z.number(),
    framing: z.enum(["avoided", "at-risk", "potential"]),
    source: z.string(),
});
export const PersonaMethodologySchema = z.object({
    title: z.string(),
    body: z.string(),
    source: z.string().optional(),
});
export const PersonaOutcomeOverrideSchema = z.object({
    stat: z.string(),
    unit: z.string(),
    delta: z.string(),
    /** Week-over-week direction relative to the metric's GOOD direction, decided
     *  by the deriver (it knows whether up is good). Null/absent = unclassified;
     *  surfaces must render neutral copy, never assume improvement. */
    trend: z.enum(["improving", "worsening", "steady"]).nullable().optional(),
    points: z.array(z.number()),
    threshold: z.number(),
    thresholdSuffix: z.string(),
    footValue: z.string(),
    costEstimate: PersonaCostEstimateSchema.nullable(),
    methodology: PersonaMethodologySchema.optional(),
    /** Real device/provider label for the metric, e.g. "Oura", "Apple Health".
     *  Derived from the signals that fed the outcome; never a fabricated device. */
    source: z.string().optional(),
});
export const PersonaHabitScoreSchema = z.object({
    label: z.string(),
    daysCompleted: z.number().int(),
    windowDays: z.number().int(),
    consecutiveDays: z.number().int(),
    isPattern: z.boolean(),
    isHabit: z.boolean(),
    verified: z
        .object({
        verdict: z.enum(["confirmed", "no-effect", "inconclusive", "insufficient-data"]),
        observedDelta: z.number(),
        sentence: z.string(),
    })
        .optional(),
});
export const PersonaSummaryOverrideSchema = z.object({
    stat: z.string(),
    statUnit: z.string(),
    statLabel: z.string(),
    tagLabel: z.string(),
    pillTone: z.enum(["good", "watch", "alert"]),
    headline: z.string(),
    text: z.string(),
    points: z.array(z.number()),
    threshold: z.number(),
    thresholdSuffix: z.string(),
    habit: PersonaHabitScoreSchema.optional(),
    methodology: PersonaMethodologySchema.optional(),
});
export const PersonaEnvironmentGraphSchema = z.object({
    kind: z.enum(["sleep", "glp1", "cardio", "generic"]),
    title: z.string(),
    meta: z.string(),
    startLabel: z.string(),
    midpointLabel: z.string().optional(),
    endLabel: z.string(),
    windowLabel: z.string().optional(),
    statusLabel: z.string().optional(),
    statusTone: z.enum(["good", "watch", "alert"]).optional(),
    nowLabel: z.string().optional(),
});
export const PersonaEnvironmentOverrideSchema = z.object({
    headlineLead: z.string(),
    headlineEmphasis: z.string(),
    graph: PersonaEnvironmentGraphSchema.optional(),
    rows: z.array(z.object({
        iconKey: z.string(),
        label: z.string(),
        sub: z.string(),
        value: z.string(),
        pill: z.string().optional(),
        level: z.enum(["good", "watch", "alert"]),
    })),
    now: z.number(),
    window: z.object({ start: z.number(), end: z.number() }).optional(),
    marks: z.array(z.object({ at: z.number(), label: z.string() })),
    footLeft: z.string(),
    footRight: z.string(),
});
export const PersonaPayloadSchema = z.object({
    personaId: z.string(),
    status: z.enum(["ok", "no-data"]),
    outcome: PersonaOutcomeOverrideSchema.nullable(),
    summary: PersonaSummaryOverrideSchema.nullable(),
    environment: PersonaEnvironmentOverrideSchema.nullable(),
    // The member's own target, formatted ("7.5h"). Carried separately from
    // `outcome` because a member with no readings yet has no outcome to attach
    // it to, and that is exactly when a surface needs to state the goal.
    goalLabel: z.string().nullable().optional(),
});
/**
 * Top-level `/api/ambient` payload. The page calls
 * `AmbientStateSchema.safeParse(response)` and either renders the
 * validated payload or surfaces a "couldn't load /today" empty state.
 *
 * Optional fields tolerate the API's transitional shapes (e.g. brief
 * may be null on a fresh account) without throwing; required fields
 * (greeting, brief.headline, score.value) are the contract floor.
 */
export const AmbientStateSchema = z.object({
    greeting: AmbientGreetingSchema,
    brief: BriefCopySchema,
    nextAction: NextActionPayloadSchema.nullable(),
    attention: z.array(z.unknown()).optional(),
    score: AmbientScorePayloadSchema.optional(),
    personaPayload: PersonaPayloadSchema.nullable().optional(),
    quietHours: QuietHoursStatusSchema.nullable().optional(),
    suggestions: z.array(z.unknown()).optional(),
    // True once the user has dismissed the deterministic companion-app
    // download card. The SPA shows that card first while this is false.
    companionAppPromptDismissed: z.boolean().optional(),
});
/**
 * Outcome trajectory payload from `/api/me/outcome`. Drives the
 * sparkline on `/today` and the per-pillar stats on `/score`.
 */
export const OutcomeBandSchema = z.object({
    id: z.enum(["on_track", "falling_behind", "at_risk"]),
    label: z.string(),
    minRate: z.number().min(0).max(1),
    maxRate: z.number().min(0).max(1),
});
export const OutcomeTrajectoryPointSchema = z.object({
    date: z.string(), // YYYY-MM-DD
    count: z.number().int().nonnegative(),
});
export const OutcomePayloadSchema = z.union([
    z.object({
        status: z.literal("no-data"),
        windowDays: z.number().int().positive(),
    }),
    z.object({
        status: z.literal("tracking"),
        windowDays: z.number().int().positive(),
        rate: z.number().min(0).max(1),
        band: OutcomeBandSchema,
        streakDays: z.number().int().nonnegative().optional(),
        totalCompletions: z.number().int().nonnegative().optional(),
        trajectory: z.array(OutcomeTrajectoryPointSchema).optional(),
    }),
]);
//# sourceMappingURL=ambient.js.map